// Pre-send sanity checks — the "are you sure?" moment other clients give you
// for a missing subject, extended to the two mistakes that actually bite:
// greeting the wrong person ("Hi Finella" to annabel@…) and promising an
// attachment that isn't there. Deterministic, no AI, runs in the composer.

export interface PreSendWarning {
  kind: 'salutation' | 'attachment' | 'subject';
  message: string;
}

// Greeting targets that name nobody in particular.
const GENERIC = new Set([
  'there', 'all', 'team', 'both', 'everyone', 'everybody', 'guys', 'folks', 'sir',
  'madam', 'sirs', 'friends', 'colleagues', 'again', 'you',
]);

function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

const TITLES = new Set(['mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'prof', 'professor', 'sir', 'lord', 'lady']);

/** The greeting line, split into candidate name words. "Hi Finella, The
 *  September…" → display ["Finella"], candidates ["Finella"]. "Hi Rob - see
 *  below" → display ["Rob"], candidates ["Rob","see","below"]: the check only
 *  warns when NO candidate matches a recipient, so trailing prose after the
 *  name can't cause a false alarm; titles and generic words are dropped. */
export function salutationNames(bodyText: string): { display: string[]; candidates: string[] } {
  const firstLine =
    bodyText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find((l) => l.length > 0) ?? '';
  const m = firstLine.match(
    /^(?:hi|hello|hey|hiya|dear|good\s+(?:morning|afternoon|evening))\s+([^\n]{1,80})/i,
  );
  if (!m) return { display: [], candidates: [] };
  const words = (chunk: string): string[] =>
    chunk
      .split(/[\s,&/]+|\band\b/i)
      .map((w) => w.replace(/[^\p{L}\p{M}'’-]/gu, ''))
      .filter((w) => w.length > 0 && !GENERIC.has(w.toLowerCase()) && !TITLES.has(w.toLowerCase()));
  // What they *meant* as the greeting: everything before the first clause
  // punctuation. Candidates are more generous (first few words of the line).
  const lead = m[1]!.split(/[,!.:;–—-]/)[0] ?? '';
  // Only treat it as a LIST of names when there's a real separator; "Hi
  // Finella The September changes…" (no comma) names one person, not three.
  const isList = /[,&/]|\band\b/i.test(lead);
  const leadWords = words(lead);
  const display = (isList ? leadWords : leadWords.slice(0, 1)).slice(0, 3);
  const candidates = words(m[1]!).slice(0, 5);
  return { display: display.length > 0 ? display : candidates.slice(0, 1), candidates };
}

/** Everything a recipient could plausibly be called: local-part pieces of the
 *  address ("annabel.smith" → annabel, smith, annabelsmith) plus words of a
 *  known display name. */
export function recipientTokens(
  addresses: string[],
  names: Record<string, string> = {},
): string[] {
  const tokens = new Set<string>();
  for (const a of addresses) {
    const email = a.trim().toLowerCase();
    const local = email.split('@')[0] ?? '';
    tokens.add(norm(local));
    for (const part of local.split(/[._\-+]+/)) if (part.length >= 2) tokens.add(norm(part));
    const name = names[email];
    if (name) for (const part of name.split(/\s+/)) if (part.length >= 2) tokens.add(norm(part));
  }
  return [...tokens].filter((t) => t.length > 0);
}

/** Loose match so nicknames and shortenings pass: "Rob" ~ "robert",
 *  "Dil" ~ "dileep", "Annabel" ~ "annabel.smith". */
function nameMatches(name: string, tokens: string[]): boolean {
  const n = norm(name);
  if (n.length < 2) return true;
  return tokens.some(
    (t) =>
      t === n ||
      (n.length >= 3 && t.startsWith(n)) ||
      (t.length >= 3 && n.startsWith(t)) ||
      (n.length >= 4 && t.includes(n)),
  );
}

function describeRecipients(addresses: string[], names: Record<string, string>): string {
  return addresses
    .slice(0, 3)
    .map((a) => {
      const email = a.trim().toLowerCase();
      const name = names[email];
      return name ? `${name} (${email})` : email;
    })
    .join(', ') + (addresses.length > 3 ? ` and ${addresses.length - 3} more` : '');
}

const ATTACHMENT_MENTION =
  /\b(attached|attachment|attachments|attaching|enclosed|enclosing|see (?:the )?(?:file|document|pdf|invoice)|find (?:the )?(?:file|document|pdf|invoice)|pfa)\b/i;

export function preSendWarnings(input: {
  bodyText: string;
  recipients: string[];
  names?: Record<string, string>;
  attachmentCount: number;
  subject?: string | null;
  checkSubject?: boolean;
}): PreSendWarning[] {
  const out: PreSendWarning[] = [];
  const names = input.names ?? {};
  const greeted = salutationNames(input.bodyText);
  if (greeted.candidates.length > 0 && input.recipients.length > 0) {
    const tokens = recipientTokens(input.recipients, names);
    const matched = greeted.candidates.some((g) => nameMatches(g, tokens));
    if (!matched) {
      out.push({
        kind: 'salutation',
        message: `You're addressing "${greeted.display.join(' and ')}", but this is going to ${describeRecipients(input.recipients, names)}.`,
      });
    }
  }
  if (input.attachmentCount === 0 && ATTACHMENT_MENTION.test(input.bodyText)) {
    out.push({
      kind: 'attachment',
      message: 'The message mentions an attachment, but nothing is attached.',
    });
  }
  if (input.checkSubject && !(input.subject ?? '').trim()) {
    out.push({ kind: 'subject', message: 'There is no subject.' });
  }
  return out;
}

/** Native confirm — reliable everywhere incl. the iPhone PWA. True = send. */
export function confirmPreSend(warnings: PreSendWarning[]): boolean {
  if (warnings.length === 0) return true;
  return window.confirm(
    `Before you send:\n\n${warnings.map((w) => `• ${w.message}`).join('\n')}\n\nSend anyway?`,
  );
}
